package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Holds the user's current coordinates as observable state (`null` = unknown / not granted). The actual
 * platform geolocation + permission request is a `suspend` lambda supplied by each platform entry point
 * through `App(getUserLocation = ...)` (same pattern as `ShareService`), so feature modules and
 * `:core:data` stay free of platform APIs. Screens call [ensureLocation] (once) to populate it and then
 * observe [location] to show "X km away"; when it stays null they fall back to the city line.
 *
 * The result is cached for the session — location is fetched at most once and the [ensureLocation] call
 * is single-flighted so concurrent card renders don't each trigger a permission prompt.
 */
class LocationController(
    private val provider: suspend () -> GeoPoint?,
) {
    private val _location = MutableStateFlow<GeoPoint?>(null)
    val location: StateFlow<GeoPoint?> = _location.asStateFlow()

    private val mutex = Mutex()
    private var attempted = false

    /** Fetch the location once per session (no-op after the first attempt, granted or not). */
    suspend fun ensureLocation() {
        if (attempted) return
        mutex.withLock {
            if (attempted) return
            attempted = true
            _location.value = runCatching { provider() }.getOrNull()
        }
    }
}
